import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import VueRouter from 'vue-router';
import 'clientjs';

import routesWithPermissions from '../router/WithPermissions';
import WithoutPermissions from '../router/WithoutPermissions';
import Tools from '@/app-model/tools';
import {firebaseAuth} from "@/firebase";
import {signInWithCustomToken} from 'firebase/auth';
import {getBrandingOptions} from "@/utils/getBrandingOptions";

Vue.use(Vuex);

const tools = new Tools;

const client = new ClientJS();
const device = (() => {
  let deviceData = JSON.parse(localStorage.getItem('device'));
  if(!deviceData) {
    deviceData = {
      device_fingerprint: client.getCustomFingerprint(
        client.getOS(),
        client.getOSVersion(),
        client.getDevice(),
        client.getDeviceType(),
        client.getDeviceVendor(),
        client.getUserAgent(),
      ),
      fingerprint: client.getFingerprint(),
    };
    localStorage.setItem('device', JSON.stringify(deviceData))
  }
  return Object.assign({
    device: [
      client.getOS(),
      client.getSystemLanguage()
    ].filter(str => str !== undefined && str !== null && str).join(' '),
    browser: [
      client.getBrowser(),
      client.getBrowserVersion(),
      client.getLanguage()
    ].filter(str => str !== undefined && str !== null && str).join(' '),
  }, deviceData);
})();

let hasResolvedCurrentUserResolver;

export default new Vuex.Store({
  state: {
    rememberMe: JSON.parse(localStorage.getItem('rememberMe')),
    refresh: JSON.parse(localStorage.getItem('refresh')),
    firebaseUser: undefined,
    // When the app first starts up, there is a small delay before Firebase auth is able to figure out if there
    // is a user currently logged in or not. Once it figures that out, this promise will resolve to either a
    // valid user, or null.
    // Note that this promise will only be resolved once - if the user somehow changes, the contents of this promise
    // will not change. Therefore, it is crucial to avoid depending on this promise's contents for any purpose other
    // than "wait until we know the state of the current user".
    hasResolvedCurrentUser: new Promise((resolve => {
      hasResolvedCurrentUserResolver = resolve;
    })),
    router: undefined,
    permissions: {
      emiot: undefined,
    },
    buildingPermissions: undefined,
    // List of global permissions
    globalPermissions: undefined,
    // Map of siteId to list of site permissions
    sitePermissionsBySiteId: {},
    sections: {
      emiot: {
        id: 1,
        base_url: window.BASE_PATH,
        content_type: 'application/json',
        permission: {
          path: 'api/permissions',
          method: 'get',
          path_template: '/api/permissions',
        },
        building_permission: {
          path: 'api/user-buildings/{building_id}/permissions',
          method: 'get',
          path_template: '/api/user-buildings/{building_id}/permissions',
        }
      },
      auth: {
        id: 5,
        endpoint_templates: ['/auth/', '/auth-api/'],
        base_url: window.AUTH_BASE_URL,
        content_type: 'application/json',
        permission: {
          path: 'auth-api/permissions',
          method: 'get',
          path_template: '/auth-api/permissions',
        }
      },
      fixture: {
        id: 7,
        base_url: window.FIXTURE_BASE_URL,
        frontend_url: window.PLEXUS_FRONTEND_URL,
        content_type: 'application/json',
        proxy_permission: 'emiot',
      },
    },
    device: device,
    account: {},
    buildings: [],
    amplitudeClient: undefined
  },
  mutations: {
    setState (state, {variable, value, saveToLocal: saveToLocal = true}) {
      Vue.set(state, variable, value);
      if (saveToLocal && state['rememberMe']) {
        localStorage.setItem(variable, JSON.stringify(value));
      }
    },
    applyState (state, {variable, value, saveToLocal: saveToLocal = true}) {
      Vue.set(state, variable, Object.assign({}, !state[variable] || !(state[variable] instanceof Object) || state[variable] instanceof Array ? {} : state[variable], value));
      if (saveToLocal && state['rememberMe']) {
        localStorage.setItem(variable, JSON.stringify(state[variable]));
      }
    },
    applyArrState (state, {variable, value, saveToLocal: saveToLocal = true, findIndex}) {
      let index = state[variable].findIndex(findIndex);
      if (index > -1) {
        Vue.set(state[variable], index, value);
      }

      if (saveToLocal && state['rememberMe']) {
        localStorage.setItem(variable, JSON.stringify(state[variable]));
      }
    },
    removeState (state, {variable, saveToLocal = true}) {
      if (saveToLocal && state['rememberMe']) {
        localStorage.removeItem(variable);
      }
      Vue.set(state, variable, undefined);
    },
  },
  getters: {
    permissions: state => ({section}) => {
      return state.permissions[section] === undefined || !state.permissions[section] ? [] : state.permissions[section];
    },
    buildingPermissions: state => !state.buildingPermissions ? {} : state.buildingPermissions,
    findSection: state => ({path_template, section}) => {
      if (section && state.sections[section]) {
        return {section};
      }
      let isAuth =  state.sections.auth.endpoint_templates.findIndex(el => path_template.indexOf(el) === 0) > -1;
      return {section: isAuth ?  'auth' : 'emiot'}
    },
    currentLoginDevice: state => () => state.device,
    buildings: state => state?.buildings?.length ? state.buildings : [],
    building: state => ({buildingId}) => {
      if (state.buildings == null) return undefined;
      buildingId = parseInt(buildingId);
      return state.buildings.find(el => el.id === buildingId);
    },
    /** @return {firebase.User} **/
    firebaseUser: state => state.firebaseUser,
    amplitudeClient: state => {
      if (state.amplitudeClient.options.userId !== state.firebaseUser.uid) {
        state.amplitudeClient.setUserId(state.firebaseUser.uid);
      }
      return state.amplitudeClient;
    },
    getSection: state => (section) => {
      return state.sections[section];
    },
    hasGlobalPermissions: (state) => ({requiredPermissions}) => {
      const permissions = state.globalPermissions === undefined ? [] : state.globalPermissions;
      return requiredPermissions.every(requiredPermission => permissions.includes(requiredPermission));
    },
    hasSitePermissions: (state) => ({siteId, requiredPermissions}) => {
      if (state.sitePermissionsBySiteId == null) return false;
      const permissions = state.sitePermissionsBySiteId[siteId] === undefined ? [] : state.sitePermissionsBySiteId[siteId];
      return requiredPermissions.every(requiredPermission => permissions.includes(requiredPermission));
    },
  },
  actions: {
    async apiResult ({dispatch, commit, state, getters, rootGetters}, {response, method, path, data, path_template = `${window.BASE_PATH}${path}`, section, building_id}) {
      let {section: endpoint_section} = getters.findSection({path_template, section});

      // Fetch site permissions if necessary
      if (endpoint_section === 'emiot' && building_id !== undefined && path_template.indexOf('/api/user-buildings/{building_id}') > -1 && getters.buildingPermissions['/api/user-buildings/' + building_id + '/permissions'] === undefined ) {
        await dispatch('fetchEmiotBuildingPermissions', {building_id})
      }

      return dispatch(
        'modernApiRequest',
        {
          section: endpoint_section,
          method,
          path,
          data,
        }
      );
    },
    redirectTologin ({dispatch, commit, state, getters, rootGetters}) {
      commit('removeState', {variable: 'refresh'});
      commit('removeState', {variable: 'token'});
      commit('removeState', {variable: 'permissions'});
      commit('removeState', {variable: 'buildings'});
      commit('removeState', {variable: 'buildingPermissions'});

      commit('removeState', {variable: 'globalPermissions'});
      commit('removeState', {variable: 'sitePermissionsBySiteId'});
      commit('removeState', {variable: 'firebaseUser'});

      const redirectPath = encodeURIComponent(state.router.history.current.fullPath.replace(/^\/(login|error)(\?.+)?$/, '/'));
      window.location = getBrandingOptions().constructMonoAuthSignInUrl(redirectPath);
    },
    legacyLogin({commit, state}, {username, password}) {
      let auth_config = state.sections.auth;
      commit('removeState', {variable: 'refresh'});
      return axios({
        validateStatus: function (status) {
          return status === 200 || status === 401 || status === 410 || status === 423
        },
        headers: {
          'Content-Type': auth_config.content_type,
        },
        method: 'POST',
        url: `${auth_config.base_url}auth/auth`,
        data: {
          username,
          password,
          username_type: 'email',
          device: device.device,
          device_fingerprint: device.device_fingerprint,
          browser: device.browser,
          fingerprint: device.fingerprint,
        }
      }).then(response => {
        if (response.status === 200) {
          commit('setState', {variable: 'refresh', value: response.data.refresh});
        }
        return response;
      })
    },
    async setFirebaseUser({dispatch, commit, state}, {user}) {
      await commit('setState', {variable: 'firebaseUser', value: user, saveToLocal: false});
      hasResolvedCurrentUserResolver();
    },
    async isLoggedIn({dispatch, commit, state}) {
      await state.hasResolvedCurrentUser;

      if (state.firebaseUser) {
        return true;
      } else {
        if (state.refresh) {
          console.log('Not logged in but has old refresh token');
          // Upgrade and sign in again
          try {
            // Exchanging old refresh token for a temporary Firebase custom token
            const upgradeResponse = await axios({
              headers: {
                'Content-Type': 'application/json',
              },
              validateStatus: function (status) {
                return [200].indexOf(status) > -1
              },
              method: 'POST',
              url: `${state.sections['auth'].base_url}auth/upgrade`,
              data: {
                refresh: state.refresh,
                device_fingerprint: device.device_fingerprint,
                fingerprint: device.fingerprint,
              },
            });
            const firebaseCustomToken = upgradeResponse.data.token;

            // Exchanging Firebase custom token for a proper Firebase refresh + id token
            const signInResult = await signInWithCustomToken(firebaseAuth, firebaseCustomToken);
            await dispatch('setFirebaseUser', {user: signInResult.user});

            // Now remove the refresh token because we won't need it any more
            commit('removeState', {variable: 'refresh'});

            console.log('Successfully upgraded old refresh token to Firebase auth session');

            return true;
          } catch (e) {
            // Failed upgrade request - refresh token is probably bad, so we're not logged in anymore
            console.warn('Failed auth upgrade request', e);
            return false;
          }
        } else {
          return false;
        }
      }
    },
    async logout({dispatch, commit}) {
      commit('removeState', {variable: 'refresh'});
      commit('removeState', {variable: 'token'});
      commit('removeState', {variable: 'permissions'});
      commit('removeState', {variable: 'buildings'});
      commit('removeState', {variable: 'buildingPermissions'});

      commit('removeState', {variable: 'globalPermissions'});
      commit('removeState', {variable: 'sitePermissionsBySiteId'});
      commit('removeState', {variable: 'firebaseUser'});

      await firebaseAuth.signOut();
      window.location = getBrandingOptions().constructMonoAuthLogoutUrl();
    },
    fetchEmiotPermissions({dispatch, commit, state}) {
      const section = 'emiot';
      let value = {};
      value[section] = undefined;

      commit('applyState', {variable: 'permissions', value, saveToLocal: false});

      let section_config = state.sections[section];

      return dispatch('modernApiRequest', {section, method: section_config.permission.method, path: section_config.permission.path})
          .then(async (response) => {
            if (response && response.status == 200) {
              let permissionSection = section;
              if (state.sections[section].proxy_permission){
                permissionSection = state.sections[section].proxy_permission;
              }
              value[permissionSection] = response.data.data;
              await commit('applyState', {variable: 'permissions', value, saveToLocal: false});
            }
            return response;
          });
    },
    fetchEmiotBuildingPermissions({dispatch, commit, state}, {building_id}) {
      const section = 'emiot';
      let section_config = state.sections[section];
      let permission_path = section_config.building_permission.path.replace('{building_id}', building_id),
          permission_path_template = section_config.building_permission.path_template.replace('{building_id}', building_id),
          value = [];

      value[permission_path_template] = undefined;
      commit('applyState', {variable: 'buildingPermissions', value: value, saveToLocal: false});

      return dispatch('modernApiRequest', {section, method: section_config.building_permission.method, path: permission_path})
          .then(async (response) => {
            if (response.status == 200) {
              value[permission_path_template] = response.data.data;
              await commit('applyState', {variable: 'buildingPermissions', value: value, saveToLocal: false})
            }
            return response
          });
    },
    /**
     * Preferred way to make an API request.
     *
     * Handles authentication (adds access token to header).
     * Assumes you have permission to perform to action. See {@link hasPermission} for checking permissions.
     *
     * @param section string describing which section to use (eg, 'emiot' or 'fixture')
     * @param method HTTP method
     * @param path relative path to section base url
     * @param data HTTP request body
     * @param canRetry true to be able to retry the request upon 401 with a new access token, false otherwise
     * @returns {AxiosPromise}
     */
    async modernApiRequest({dispatch, commit, state, getters}, {section, method, path, data, canRetry = true}) {
      await state.hasResolvedCurrentUser;

      // Ensure we're current logged in
      if (!state.firebaseUser) {
        dispatch('logout');
        throw 'Not logged in';
      }
      const idToken = await state.firebaseUser.getIdToken();

      // Now that we have an access token, add it to the headers
      const headers = {
        'Content-Type': state.sections[section].content_type,
        'Authorization': `Bearer ${idToken}`,
      };

      // Make the actual request
      return axios({
        headers,
        validateStatus: function (status) {
          return [200, 201, 204, 400, 401, 409, 410, 422, 423].indexOf(status) > -1
        },
        method: method,
        url: `${state.sections[section].base_url}${path}`,
        data: data
      })
          .then(response => {
            // If token expired, retry one more time
            if (response.status === 401 && canRetry) {
              return dispatch('modernApiRequest', {section, method, path, data, canRetry: false});
            } else {
              return response;
            }
          });
    },
    /**
     * Preferred way to make an unauthenticated API request.
     *
     * @param section string describing which section to use (eg, 'emiot' or 'fixture')
     * @param method HTTP method
     * @param path relative path to section base url
     * @param data HTTP request body
     * @returns {AxiosPromise}
     */
    async modernApiRequestNoAuth({dispatch, commit, state, getters}, {section, method, path, data}) {
      const headers = {
        'Content-Type': state.sections[section].content_type,
      };

      // Make the actual request
      return axios({
        headers,
        validateStatus: function (status) {
          return [200, 201, 204, 400, 401, 409, 410, 422, 423].indexOf(status) > -1
        },
        method: method,
        url: `${state.sections[section].base_url}${path}`,
        data: data
      });
    },
    async fetchGlobalPermissions({dispatch, state, commit}) {
      let permissions = state.globalPermissions;
      if (permissions === undefined) {
        // Set the state to something that isn't undefined, to prevent multiple invocations of this function from making requests at the same time
        // commit('setState', {variable: 'globalPermissions', value: [], saveToLocal: false});
        permissions = await dispatch('modernApiRequest', {
          section: 'fixture',
          method: 'GET',
          path: 'api/v1/me',
        })
            .then(response => {
              return response.data.globalRole.permissions;
            });
        commit('setState', {variable: 'globalPermissions', value: permissions, saveToLocal: false});
      }
      return permissions;
    },
    async fetchSitePermissions({dispatch, state, commit}, {siteId}) {
      let permissions = state.sitePermissionsBySiteId == null ? undefined : state.sitePermissionsBySiteId[siteId];
      if (permissions === undefined) {
        // Set the state to something that isn't undefined, to prevent multiple invocations of this function from making requests at the same time
        // commit('applyState', {variable: 'sitePermissionsBySiteId', value: {[siteId]: []}, saveToLocal: false});
        permissions = await dispatch('modernApiRequest', {
          section: 'fixture',
          method: 'GET',
          path: `api/v1/permissions/sites/${siteId}/users/${state.firebaseUser.uid}`,
        })
            .then(response => {
              return response.data.siteRole.permissions;
            });
        commit('applyState', {variable: 'sitePermissionsBySiteId', value: {[siteId]: permissions}, saveToLocal: false});
      }
      return permissions;
    },
    reloadRouter ({dispatch, commit, state, getters, rootGetters}) {
      let routes = routesWithPermissions({dispatch, commit, state, getters, rootGetters});
      return new VueRouter({mode: 'history', routes});
    },
    clearRouter ({dispatch, commit, state, getters, rootGetters}) {
      let routes = WithoutPermissions({dispatch, commit, state, getters, rootGetters});
      return new VueRouter({mode: 'history', routes});
    }
  }
})